<template>
    <div class="qna-body">
        <div v-if="!initialMessage && aiEvItem && aiEvItem.reason" class="ai-summary">
            <div class="d-flex align-items-center mb-1" style="gap: 8px;">
                <strong class="text-secondary small">{{ $t('AI 판정') }}</strong>
                <b-badge :variant="aiEvItem.is_satisfied ? 'success' : 'danger'">
                    {{ aiEvItem.is_satisfied ? $t('충족 (Y)') : $t('미충족 (N)') }}
                </b-badge>
            </div>
            <div v-if="aiSummaryComment" class="summary-comment">{{ aiSummaryComment }}</div>
        </div>

        <div ref="chatScroll" class="chat-scroll">
            <div v-if="loading" class="text-center py-5">
                <b-spinner label="Loading..."/>
                <div class="mt-2 text-muted small">{{ $t('대화 이력을 불러오는 중입니다...') }}</div>
            </div>

            <div v-else-if="loadError" class="text-center py-5">
                <div class="text-danger mb-2">{{ loadError }}</div>
                <b-button size="sm" variant="outline-secondary" :disabled="sending" @click="loadHistory()">
                    {{ $t('다시 시도') }}
                </b-button>
            </div>

            <template v-else>
                <AIQnaInitialEvaluation v-if="initialMessage"
                                        :initial-message="initialMessage"
                                        class="mb-3"
                />

                <div v-if="!initialMessage && items.length === 0 && !sending"
                     class="text-center text-muted py-5">
                    {{ $t('아직 대화가 없습니다. AI 판정에 대해 궁금한 점을 질문해보세요.') }}
                </div>

                <template v-for="msg in items">
                    <div :key="`qna-msg-${msg.id}`"
                         :class="['chat-row', msg.role === 'user' ? 'user' : 'assistant']"
                    >
                        <div class="bubble">
                            <div class="content">{{ msg.content }}</div>
                            <div class="meta">
                                <b-badge v-if="msg.degraded"
                                         v-b-tooltip.hover="$t('이 항목의 판정 컨텍스트는 재구성본이라 답변 정확도가 낮을 수 있습니다')"
                                         variant="warning"
                                >
                                    {{ $t('재구성 컨텍스트') }}
                                </b-badge>
                                <span class="time">{{ msg.created_at || $t('방금') }}</span>
                            </div>
                        </div>
                    </div>
                    <AIQnaRevisionProposal v-if="revisionForMessage(msg)"
                                           :key="`qna-revision-${revisionForMessage(msg).id}`"
                                           :revision="revisionForMessage(msg)"
                                           :busy="decisionBusy || sending"
                                           class="mb-3"
                                           @apply="decideRevision('apply')"
                                           @reject="decideRevision('reject')"
                    />
                </template>

                <AIQnaRevisionProposal v-if="hasUnlinkedPendingRevision"
                                       :revision="pendingRevision"
                                       :busy="decisionBusy || sending"
                                       class="mb-3"
                                       @apply="decideRevision('apply')"
                                       @reject="decideRevision('reject')"
                />

                <div v-if="revisionHistory.length" class="revision-history mb-3">
                    <b-button v-b-toggle="revisionHistoryCollapseId"
                              block
                              size="sm"
                              variant="outline-secondary">
                        {{ $t('수정안 이력') }} ({{ revisionHistory.length }})
                    </b-button>
                    <b-collapse :id="revisionHistoryCollapseId" class="mt-2">
                        <div v-for="revision in revisionHistory"
                             :key="`revision-history-${revision.id}`"
                             class="history-row">
                            <b-badge :variant="revisionStatus(revision.status).variant">
                                {{ $t(revisionStatus(revision.status).label) }}
                            </b-badge>
                            <span class="ml-2 text-muted small">
                                {{ revision.decidedAt || revision.createdAt || '-' }}
                            </span>
                        </div>
                    </b-collapse>
                </div>

                <div v-if="sending" class="chat-row assistant">
                    <div class="bubble pending">
                        <b-spinner small class="mr-2"/>
                        {{ $t('AI가 답변을 생성 중입니다... (최대 1분가량 걸릴 수 있습니다)') }}
                    </div>
                </div>
            </template>
        </div>

        <div class="composer">
            <b-alert :show="qnaUnsupported" variant="warning" class="mb-2 py-1 px-2 small">
                {{ $t('이 평가항목은 후속 Q&A를 지원하지 않습니다 (AI 미호출 항목).') }}
            </b-alert>
            <b-alert :show="turnsExhausted && !qnaUnsupported" variant="warning" class="mb-2 py-1 px-2 small">
                {{ $t('Q&A 턴수 상한(20회)에 도달하여 더 이상 질문할 수 없습니다.') }}
            </b-alert>

            <b-form-textarea v-model="question"
                             :placeholder="$t('질문을 입력하세요. (Ctrl+Enter로 전송)')"
                             :rows="2"
                             :max-rows="4"
                             :disabled="sending || decisionBusy || loading || turnsExhausted || qnaUnsupported || loadError != null"
                             @keydown.ctrl.enter.exact.prevent="send"
            />
            <div class="d-flex justify-content-between align-items-center mt-1">
                <span class="text-muted small">{{ $t('질문') }} {{ userTurnCount }}/{{ MAX_TURNS }}</span>
                <div class="d-flex align-items-center" style="gap: 12px;">
                    <span :class="['small', trimmedLength > MAX_QUESTION_LENGTH ? 'text-danger' : 'text-muted']">
                        {{ trimmedLength }} / {{ MAX_QUESTION_LENGTH }}
                    </span>
                    <b-button variant="primary" size="sm" :disabled="!canSend" @click="send">
                        <b-spinner v-if="sending" small class="mr-1"/>
                        <fa v-else :icon="['fas', 'paper-plane']" class="mr-1"/>
                        {{ $t('전송') }}
                    </b-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
import axios from '@core_utils/api/_axios';
import AIQnaInitialEvaluation from './AIQnaInitialEvaluation.vue';
import AIQnaRevisionProposal from './AIQnaRevisionProposal.vue';
import presentation from './aiQnaPresentation';

const {
    canApplyRevision,
    canRejectRevision,
    confirmedUserTurnCount,
    createBodylessRevisionRequest,
    isRecoveredQuestion,
    normalizeInitialMessage,
    normalizeQnaPayload,
    normalizeQnaPostResponse,
    normalizeRevision,
    revisionStatusMeta,
} = presentation;

const MAX_TURNS = 20;
const MAX_QUESTION_LENGTH = 2000;

// AI 평가 (audit, ev) 후속 Q&A 대화 본문. 모달(AIQnaChatModal)과 AI 검토 화면 상세 패널이 같이 쓴다.
export default {
    name: 'AIQnaChatPanel',
    components: {AIQnaInitialEvaluation, AIQnaRevisionProposal},
    props: {
        // true 가 되면 대화를 불러오고, false 가 되면 진행 중 응답을 무효화하고 비운다.
        active: {
            type: Boolean,
            default: true,
        },
        aiAuditId: {
            type: [Number, String],
            default: null,
        },
        evId: {
            type: [Number, String],
            default: null,
        },
        // valid_map 원소 (판정 요약 표시용) — {is_satisfied, reason, converted_reason, ...}
        aiEvItem: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            MAX_TURNS,
            MAX_QUESTION_LENGTH,
            shown: false,
            loading: false,
            loadError: null,
            loadSeq: 0,
            sending: false,
            sendSeq: 0,
            decisionBusy: false,
            decisionSeq: 0,
            question: '',
            items: [],
            initialMessage: null,
            pendingRevision: null,
            revisionHistory: [],
            qnaUnsupported: false,
        };
    },
    computed: {
        busy() {
            return this.sending || this.decisionBusy;
        },
        aiSummaryComment() {
            return this.aiEvItem?.reason?.comment
                || this.aiEvItem?.converted_reason?.audit_comment
                || '';
        },
        trimmedLength() {
            return this.question.trim().length;
        },
        userTurnCount() {
            // 표시용 카운터 — 전송 중인 낙관적 말풍선도 포함해 즉각 반영
            return this.items.filter((i) => i.role === 'user').length;
        },
        turnsExhausted() {
            // 상한 판정은 서버 확정 턴만 기준 — 전송 중(낙관적 포함) 상태에서
            // "상한 도달" 안내가 잘못 깜빡이지 않도록 한다 (실패 시 되돌아옴).
            const confirmed = confirmedUserTurnCount(this.items);
            return confirmed >= MAX_TURNS;
        },
        hasUnlinkedPendingRevision() {
            if (!this.pendingRevision) return false;
            return !this.items.some((item) => this.revisionForMessage(item));
        },
        revisionHistoryCollapseId() {
            return `ai-qna-panel-${this._uid}-revision-history`;
        },
        canSend() {
            // loadError 게이팅: 히스토리 미동기화 상태에서 전송하면 턴 카운터가
            // 어긋나고, 성공해도 에러 패널이 대화를 가린다. 재시도로 먼저 해소해야 함.
            return !this.sending
                && !this.decisionBusy
                && !this.loading
                && this.loadError == null
                && !this.qnaUnsupported
                && !this.turnsExhausted
                && this.aiAuditId != null
                && this.evId != null
                && this.trimmedLength > 0
                && this.trimmedLength <= MAX_QUESTION_LENGTH;
        },
    },
    watch: {
        active: {
            immediate: true,
            handler(value) {
                if (value) this.onShow();
                else if (this.shown) this.onHidden();
            },
        },
        // 열린 상태에서 부모가 대상(ev)을 바꾸면 대화 리로드
        aiAuditId() {
            if (this.shown) this.resetAndLoad();
        },
        evId() {
            if (this.shown) this.resetAndLoad();
        },
        // 부모가 닫기/탭 전환을 막을 수 있도록 전송·결정 진행 여부를 알린다.
        busy(value) {
            this.$emit('busy-change', value);
        },
    },
    beforeDestroy() {
        this.onHidden();
        // 파괴 중에는 busy watcher 가 돌지 않으므로 부모 잠금을 직접 풀어준다.
        this.$emit('busy-change', false);
    },
    methods: {
        onShow() {
            this.shown = true;
            this.resetAndLoad();
        },
        onHidden() {
            this.shown = false;
            this.loadSeq += 1; // 진행 중인 조회 결과 무효화
            this.sendSeq += 1; // 진행 중인 질문 응답 무효화
            this.decisionSeq += 1; // 진행 중인 적용/거절 결과 무효화
            this.loading = false;
            this.loadError = null;
            this.sending = false;
            this.decisionBusy = false;
            this.question = '';
            this.items = [];
            this.initialMessage = null;
            this.pendingRevision = null;
            this.revisionHistory = [];
            this.qnaUnsupported = false;
        },
        resetAndLoad() {
            this.loadSeq += 1;
            this.sendSeq += 1;
            this.decisionSeq += 1;
            this.question = '';
            this.items = [];
            this.initialMessage = null;
            this.pendingRevision = null;
            this.revisionHistory = [];
            this.sending = false;
            this.decisionBusy = false;
            this.qnaUnsupported = false;
            this.loadError = null;
            this.loadHistory();
        },
        isSameTarget(auditId, evId) {
            return this.shown
                && String(this.aiAuditId) === String(auditId)
                && String(this.evId) === String(evId);
        },
        isSameSendTarget(seq, auditId, evId) {
            return seq === this.sendSeq && this.isSameTarget(auditId, evId);
        },
        async loadHistory({silent = false} = {}) {
            if (this.aiAuditId == null || this.evId == null) return false;
            const seq = ++this.loadSeq;
            if (!silent) {
                this.loading = true;
                this.loadError = null;
            }
            try {
                const res = await axios.get(`/assessments/ai-audit/${this.aiAuditId}/qna`, {
                    params: {ev_id: Number(this.evId)},
                });
                if (seq !== this.loadSeq) return false;
                const payload = normalizeQnaPayload(res?.data);
                this.items = payload.items;
                this.initialMessage = payload.initialMessage;
                this.pendingRevision = payload.pendingRevision;
                this.revisionHistory = payload.revisionHistory;
                this.scrollToBottom();
                return true;
            } catch (err) {
                if (seq !== this.loadSeq) return false;
                if (!silent) this.loadError = this.qnaErrorMessage(err);
                return false;
            } finally {
                // 최신 seq 소유자가 항상 loading을 정리한다. silent 조회가 비-silent
                // 조회를 추월(seq bump)한 경우에도 stale true가 남지 않도록 !silent
                // 조건을 두지 않는다 (추월당한 조회는 여기 조건에서 제외됨).
                if (seq === this.loadSeq) this.loading = false;
            }
        },
        async send() {
            if (!this.canSend) return;
            const scopedAuditId = this.aiAuditId;
            const scopedEvId = this.evId;
            const seq = ++this.sendSeq;
            const questionText = this.question.trim();

            // 전송 즉시 내 질문을 말풍선으로 먼저 표시(낙관적 append)하고 입력창을
            // 비운다. 201 도착 시 서버 행으로 교체, 실패 시 제거 + 입력 복원.
            const tempId = `optimistic-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            this.items.push({
                id: tempId,
                role: 'user',
                content: questionText,
                created_at: null,
                optimistic: true,
            });
            this.question = '';
            this.scrollToBottom();

            this.sending = true;
            try {
                const res = await axios.post(`/assessments/ai-audit/${scopedAuditId}/qna`, {
                    ev_id: Number(scopedEvId),
                    question: questionText,
                }, {
                    // ALB idle timeout(60초)보다 넉넉한 클라이언트 상한 — hung 소켓에서도
                    // sending이 반드시 해제되도록 보장 (미설정 시 무기한 대기).
                    timeout: 90000,
                });
                if (!this.isSameSendTarget(seq, scopedAuditId, scopedEvId)) return;
                this.removeOptimistic(tempId);
                const data = normalizeQnaPostResponse(res?.data);
                // 동시 GET(재조회)이 먼저 같은 행을 실어왔을 수 있으므로 id 중복 방지
                if (data.question && !this.items.some((m) => m.id === data.question.id)) {
                    this.items.push({
                        id: data.question.id,
                        role: 'user',
                        content: data.question.content,
                        created_at: data.question.created_at || null,
                    });
                }
                if (data.answer && !this.items.some((m) => m.id === data.answer.id)) {
                    this.items.push({
                        id: data.answer.id,
                        role: 'assistant',
                        content: data.answer.content,
                        created_at: data.answer.created_at || null,
                        degraded: data.answer.degraded === true,
                    });
                }
                if (data.revisionProposal) {
                    this.pendingRevision = data.revisionProposal.qnaAnswerId != null || !data.answer
                        ? data.revisionProposal
                        : {...data.revisionProposal, qnaAnswerId: data.answer.id};
                }
                this.scrollToBottom();
            } catch (err) {
                if (!this.isSameSendTarget(seq, scopedAuditId, scopedEvId)) return;
                await this.handleSendError(err, {questionText, tempId, scopedAuditId, scopedEvId, seq});
            } finally {
                if (seq === this.sendSeq) this.sending = false;
            }
        },
        removeOptimistic(tempId) {
            this.items = this.items.filter((m) => m.id !== tempId);
        },
        restoreQuestion(questionText) {
            // 입력창은 전송 중 비활성이라 비어 있는 것이 정상이지만, 만약 사용자
            // 입력이 있다면 덮어쓰지 않는다.
            if (!this.question.trim()) this.question = questionText;
        },
        async handleSendError(err, {questionText, tempId, scopedAuditId, scopedEvId, seq}) {
            const status = err?.response?.status;

            // ALB 60초 idle timeout 등: 서버가 백그라운드에서 처리를 끝내고 저장했을 수
            // 있으므로, 재전송 유도 전에 히스토리를 재조회해 정합성을 복구한다 (§1-4-2).
            const isTimeoutLike = !err?.response || status === 504;
            if (isTimeoutLike) {
                const recovered = await this.reloadAndCheckRecovered(questionText);
                // await 사이에 모달이 닫히거나 대상이 바뀌었으면 이 세션의 상태를
                // 건드리지 않는다 (닫힘/전환 시 onHidden·resetAndLoad가 이미 정리함).
                if (!this.isSameSendTarget(seq, scopedAuditId, scopedEvId)) return;
                // 재조회 성공 시 items가 서버본으로 통째로 교체되어 낙관적 말풍선은
                // 이미 사라졌고, 재조회가 실패(무변경)한 경우 남아 있으므로 명시 제거.
                this.removeOptimistic(tempId);
                if (recovered) {
                    this.toast(this.$t('네트워크 지연으로 응답이 늦었지만, 답변이 저장되어 대화를 갱신했습니다.'), 'warning');
                } else {
                    this.restoreQuestion(questionText);
                    this.toast(this.$t('네트워크 오류로 답변을 받지 못했습니다. 잠시 후 다시 시도하세요.'), 'danger');
                }
                return;
            }

            if (status === 422) {
                this.qnaUnsupported = true;
            }
            if (status === 429) {
                // 사유 구분: 동시요청 처리 중 vs 턴수 상한. 상한이면 카운터 동기화.
                const text = getErrorMessage(err, '');
                if (text.includes('상한')) {
                    await this.loadHistory({silent: true});
                    if (!this.isSameSendTarget(seq, scopedAuditId, scopedEvId)) return;
                }
            }
            // 실패한 질문은 말풍선에서 내리고 입력창에 복원해 재시도 가능하게 한다.
            this.removeOptimistic(tempId);
            this.restoreQuestion(questionText);
            this.toast(this.qnaErrorMessage(err), 'danger');
        },
        async reloadAndCheckRecovered(questionText) {
            // 턴수 증가 비교는 stale 기준선(초기 GET 실패, 타 세션 병행)에서 오판하므로
            // 내용 일치로 판정한다. 서버는 저장 전 strip하므로 trim된 원문과 직접 비교 가능.
            // ⚠️ loadHistory는 내부에서 에러를 삼키므로(silent GET 실패 시 items 무변경),
            // 낙관적 말풍선(content === questionText)이 자기 자신과 매칭되어 false-positive가
            // 나지 않도록 반드시 서버 행(!optimistic)만 검사한다.
            await this.loadHistory({silent: true});
            return isRecoveredQuestion(this.items, questionText);
        },
        revisionForMessage(message) {
            if (!this.pendingRevision || message?.role !== 'assistant') return null;
            if (this.pendingRevision.qnaAnswerId == null) return null;
            return String(this.pendingRevision.qnaAnswerId) === String(message.id)
                ? this.pendingRevision
                : null;
        },
        revisionStatus(status) {
            return revisionStatusMeta(status);
        },
        async decideRevision(action) {
            const revision = this.pendingRevision;
            const isAllowed = action === 'apply'
                ? canApplyRevision(revision, this.decisionBusy || this.sending)
                : canRejectRevision(revision, this.decisionBusy || this.sending);
            if (!isAllowed) return;

            const scopedAuditId = this.aiAuditId;
            const scopedEvId = this.evId;
            const scopedRevisionId = revision.id;
            const seq = ++this.decisionSeq;
            const isApply = action === 'apply';
            const confirmed = await this.$bvModal.msgBoxConfirm(
                isApply
                    ? this.$t('이 수정안을 AI 평가 결과에 적용하시겠습니까?')
                    : this.$t('이 수정안을 거절하시겠습니까?'),
                {
                    title: isApply ? this.$t('수정안 적용') : this.$t('수정안 거절'),
                    okTitle: isApply ? this.$t('적용') : this.$t('거절'),
                    cancelTitle: this.$t('취소'),
                    okVariant: isApply ? 'primary' : 'danger',
                    centered: true,
                },
            );
            if (!confirmed || !this.isSameDecisionTarget(seq, scopedAuditId, scopedEvId, scopedRevisionId)) return;

            this.decisionBusy = true;
            try {
                const requestConfig = createBodylessRevisionRequest(scopedAuditId, scopedRevisionId, action);
                const res = await axios(requestConfig);
                if (!this.isSameDecisionTarget(seq, scopedAuditId, scopedEvId, scopedRevisionId)) return;

                const decidedRevision = normalizeRevision(res?.data?.revision);
                if (decidedRevision) {
                    this.pendingRevision = {...this.pendingRevision, ...decidedRevision};
                    this.revisionHistory = [
                        this.pendingRevision,
                        ...this.revisionHistory.filter((item) => String(item.id) !== String(scopedRevisionId)),
                    ];
                }

                if (isApply && res?.data?.current_evaluation) {
                    this.initialMessage = normalizeInitialMessage({
                        kind: 'evaluation_snapshot',
                        source: 'revision',
                        is_reconstructed: false,
                        current_evaluation: res.data.current_evaluation,
                    });
                }

                await this.loadHistory({silent: true});
                if (!this.isSameDecisionTarget(seq, scopedAuditId, scopedEvId, scopedRevisionId, {allowMissingRevision: true})) return;
                if (isApply) {
                    this.$emit('revision-applied', {
                        ai_audit_id: scopedAuditId,
                        ev_id: scopedEvId,
                        revision_id: scopedRevisionId,
                    });
                }
                this.toast(
                    isApply ? this.$t('수정안을 적용했습니다.') : this.$t('수정안을 거절했습니다.'),
                    'success',
                );
            } catch (err) {
                if (!this.isSameDecisionTarget(seq, scopedAuditId, scopedEvId, scopedRevisionId, {allowMissingRevision: true})) return;
                const status = err?.response?.status;
                this.toast(this.revisionErrorMessage(err), 'danger');
                if ([404, 409].includes(status)) {
                    await this.loadHistory({silent: true});
                }
            } finally {
                if (seq === this.decisionSeq) this.decisionBusy = false;
            }
        },
        isSameDecisionTarget(seq, auditId, evId, revisionId, {allowMissingRevision = false} = {}) {
            if (seq !== this.decisionSeq || !this.isSameTarget(auditId, evId)) return false;
            if (allowMissingRevision && !this.pendingRevision) return true;
            return String(this.pendingRevision?.id) === String(revisionId);
        },
        revisionErrorMessage(err) {
            const status = err?.response?.status;
            const rawText = getErrorMessage(err, '');
            const serverText = rawText && !/<(?:html|!doctype)/i.test(rawText) ? rawText : '';
            const exposedServerText = [400, 404, 409, 422].includes(status) ? serverText : '';
            const fallback = {
                400: this.$t('수정안 결정 요청이 올바르지 않습니다.'),
                401: this.$t('로그인이 만료되었습니다. 다시 로그인해 주세요.'),
                403: this.$t('접근 권한이 없습니다. (관리자 레벨 3 이상)'),
                404: this.$t('수정안을 찾을 수 없어 최신 상태를 다시 불러옵니다.'),
                409: this.$t('수정안 상태가 변경되어 최신 상태를 다시 불러옵니다.'),
                422: this.$t('이 수정안은 검증에 실패하여 적용할 수 없습니다.'),
            };
            if ([404, 409].includes(status) && exposedServerText) {
                return `${exposedServerText} ${this.$t('최신 상태를 다시 불러옵니다.')}`;
            }
            return exposedServerText || fallback[status] || this.$t('수정안 처리 중 오류가 발생했습니다.');
        },
        qnaErrorMessage(err) {
            const status = err?.response?.status;
            // 400/404/422/429/502/503 바디는 plain text, 401/403만 JSON (§1-3).
            const serverText = getErrorMessage(err, '');

            // Q&A API의 plain-text 서버 메시지는 사용자에게 그대로 전달한다.
            if ([400, 404, 413, 422, 429, 502, 503].includes(status) && serverText) {
                return serverText;
            }
            const fallback = {
                400: this.$t('질문 입력값이 올바르지 않습니다.'),
                401: this.$t('로그인이 만료되었습니다. 다시 로그인해 주세요.'),
                403: this.$t('접근 권한이 없습니다. (관리자 레벨 3 이상)'),
                404: this.$t('AI 평가 정보를 찾을 수 없습니다.'),
                413: this.$t('질문 데이터가 너무 큽니다.'),
                422: this.$t('이 평가항목은 후속 Q&A를 지원하지 않습니다.'),
                429: this.$t('요청이 처리 중이거나 턴수 상한에 도달했습니다. 잠시 후 다시 시도하세요.'),
                502: this.$t('AI 답변 생성에 실패했습니다. 잠시 후 다시 시도하세요.'),
                503: this.$t('AI 답변 생성이 지연되고 있습니다. 잠시 후 다시 시도하세요.'),
            };
            return fallback[status] || this.$t('Q&A 처리 중 오류가 발생했습니다.');
        },
        toast(message, variant) {
            this.$bvToast.toast(message, {
                title: this.$t('AI 평가 Q&A'),
                variant,
                solid: true,
            });
        },
        scrollToBottom() {
            this.$nextTick(() => {
                const el = this.$refs.chatScroll;
                if (el) el.scrollTop = el.scrollHeight;
            });
        },
    },
};
</script>

<style scoped lang="scss">
// 높이는 감싸는 쪽(모달 / 검토 화면 상세 패널)이 정한다.
.qna-body {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
}

.ai-summary {
    padding: 10px 16px;
    background-color: #fafbfc;
    border-bottom: 1px solid #e9ecef;

    .summary-comment {
        font-size: 0.8rem;
        line-height: 1.4;
        color: #495057;
        white-space: pre-wrap;
        max-height: 96px;
        overflow-y: auto;
    }
}

.chat-scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 12px 16px;
}

.revision-history {
    border-top: 1px solid #e9ecef;
    padding-top: 10px;

    .history-row {
        display: flex;
        align-items: center;
        padding: 6px 8px;
        border-bottom: 1px solid #f1f3f5;
    }
}

.chat-row {
    display: flex;
    margin-bottom: 10px;

    &.user {
        justify-content: flex-end;
    }

    &.assistant {
        justify-content: flex-start;
    }

    .bubble {
        max-width: 78%;
        padding: 8px 12px;
        border-radius: 10px;
        font-size: 0.88rem;
        line-height: 1.5;

        .content {
            white-space: pre-wrap;
            word-break: break-word;
        }

        .meta {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 6px;
            margin-top: 4px;

            .time {
                font-size: 0.7rem;
                color: #868e96;
            }
        }

        &.pending {
            background-color: #f1f3f5;
            color: #495057;
            font-size: 0.82rem;
        }
    }

    &.user .bubble {
        background-color: #e7f1ff;
        color: #212529;
    }

    &.assistant .bubble {
        background-color: #f1f3f5;
        color: #212529;
    }
}

.composer {
    flex: 0 0 auto;
    padding: 10px 16px 12px;
    border-top: 1px solid #e9ecef;
}
</style>
